import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  AlertTriangle, Clock, TrendingDown, TrendingUp, Users, IndianRupee, CalendarClock,
  Stethoscope, Ambulance, Briefcase, Target, LineChart as LineChartIcon,
  ChevronRight, Award, Zap, Percent, Flame, Sparkles, Snowflake, Wallet, Info,
  Settings, Plus, Trash2, X, Save, Loader2, HelpCircle,
} from 'lucide-react';
import { cn } from '../../utils/tailwindUtils';
import { getEngageStatusBadge, getEngageSourceBadge, ENGAGE_STATUS_LABEL, ENGAGE_SOURCE_LABEL } from '../../utils/engageStageBadge';
import useApiMethods from '../../lib/useApiMethods';
import ToastMsg from '../../components/common/ToastMsg';


const formatINR = (n) => Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 });
const fmtDate   = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';


/**
 * Small hover tooltip. Wraps any element; shows `content` on hover.
 * `content` can be a string or JSX. `side` = 'top' | 'bottom' | 'right' | 'left'.
 */
export function InfoTooltip({ children, content, side = 'top', className }) {
  const posClass = {
    top:    'bottom-full mb-2 left-1/2 -translate-x-1/2',
    bottom: 'top-full mt-2 left-1/2 -translate-x-1/2',
    right:  'left-full ml-2 top-1/2 -translate-y-1/2',
    left:   'right-full mr-2 top-1/2 -translate-y-1/2',
  }[side];
  return (
    <span className={cn('relative inline-flex items-center group', className)}>
      {children}
      <span
        role="tooltip"
        className={cn(
          'pointer-events-none absolute z-50 invisible opacity-0 group-hover:visible group-hover:opacity-100 transition-opacity duration-100',
          'bg-gray-900 text-white text-[11px] leading-relaxed rounded-lg shadow-xl px-3 py-2 whitespace-normal min-w-[200px] max-w-[320px]',
          posClass,
        )}
      >
        {content}
      </span>
    </span>
  );
}


/** Small "(?)" icon that triggers an InfoTooltip. */
function HelpBadge({ content, side = 'top' }) {
  return (
    <InfoTooltip content={content} side={side}>
      <HelpCircle className="w-3 h-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 cursor-help dark:text-gray-600" />
    </InfoTooltip>
  );
}


// ────────────────────────────────────────────────────────────────────────────
// Alerts — aging enquiries + overdue follow-ups
// ────────────────────────────────────────────────────────────────────────────

export function AlertsPanel({ alerts }) {
  if (!alerts) return null;
  const aging = alerts.aging_count || 0;
  const overdue = alerts.overdue_followups || 0;
  if (aging === 0 && overdue === 0) return null;

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
      {/* Overdue follow-ups */}
      <div className={cn(
        'rounded-xl p-4 border shadow-sm',
        overdue > 0 ? 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
                    : 'bg-white dark:bg-gray-800 border-gray-100 dark:border-gray-700',
      )}>
        <div className="flex items-start justify-between mb-2">
          <div className="flex items-center gap-2">
            <div className={cn('w-8 h-8 rounded-lg flex items-center justify-center',
              overdue > 0 ? 'bg-red-100 text-red-600 dark:bg-red-900/40 dark:text-red-400'
                          : 'bg-gray-100 text-gray-500')}>
              <AlertTriangle className="w-4 h-4" />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400 font-medium">
                Overdue follow-ups
              </div>
              <div className={cn('text-2xl font-bold', overdue > 0 ? 'text-red-600 dark:text-red-400' : 'text-gray-500')}>
                {overdue}
              </div>
            </div>
          </div>
          <Link to="/engage/followups" className="text-[11px] text-red-600 dark:text-red-400 hover:underline flex items-center gap-0.5">
            Work them <ChevronRight className="w-3 h-3" />
          </Link>
        </div>
        {(alerts.overdue_samples || []).slice(0, 3).map(s => (
          <Link key={s.id} to={`/engage/enquiries/${s.id}`}
            className="block px-2 py-1.5 rounded hover:bg-red-100/50 dark:hover:bg-red-900/20 text-[12px] text-gray-700 dark:text-gray-300">
            <span className="font-medium">{s.name}</span>
            <span className="text-gray-500 dark:text-gray-400"> · overdue {s.days_overdue}d</span>
            <span className={cn('float-right px-1.5 py-0.5 rounded text-[10px]', getEngageStatusBadge(s.status))}>
              {ENGAGE_STATUS_LABEL[s.status] || s.status}
            </span>
          </Link>
        ))}
      </div>

      {/* Aging enquiries */}
      <div className={cn(
        'rounded-xl p-4 border shadow-sm',
        aging > 0 ? 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800'
                  : 'bg-white dark:bg-gray-800 border-gray-100 dark:border-gray-700',
      )}>
        <div className="flex items-start justify-between mb-2">
          <div className="flex items-center gap-2">
            <div className={cn('w-8 h-8 rounded-lg flex items-center justify-center',
              aging > 0 ? 'bg-amber-100 text-amber-600 dark:bg-amber-900/40 dark:text-amber-400'
                        : 'bg-gray-100 text-gray-500')}>
              <Clock className="w-4 h-4" />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400 font-medium">
                Stale enquiries (&gt; 7d no activity)
              </div>
              <div className={cn('text-2xl font-bold', aging > 0 ? 'text-amber-600 dark:text-amber-400' : 'text-gray-500')}>
                {aging}
              </div>
            </div>
          </div>
          <Link to="/engage/enquiries" className="text-[11px] text-amber-600 dark:text-amber-400 hover:underline flex items-center gap-0.5">
            Review <ChevronRight className="w-3 h-3" />
          </Link>
        </div>
        {(alerts.aging_samples || []).slice(0, 3).map(s => (
          <Link key={s.id} to={`/engage/enquiries/${s.id}`}
            className="block px-2 py-1.5 rounded hover:bg-amber-100/50 dark:hover:bg-amber-900/20 text-[12px] text-gray-700 dark:text-gray-300">
            <span className="font-medium">{s.name}</span>
            <span className="text-gray-500 dark:text-gray-400"> · stale {s.days_stale}d</span>
            <span className={cn('float-right px-1.5 py-0.5 rounded text-[10px]', getEngageStatusBadge(s.status))}>
              {ENGAGE_STATUS_LABEL[s.status] || s.status}
            </span>
          </Link>
        ))}
      </div>
    </div>
  );
}


// ────────────────────────────────────────────────────────────────────────────
// Enquiry funnel (last 30 days)
// ────────────────────────────────────────────────────────────────────────────

const FUNNEL_TONES = [
  'from-blue-500 to-cyan-500',
  'from-cyan-500 to-teal-500',
  'from-purple-500 to-pink-500',
  'from-orange-500 to-amber-500',
  'from-indigo-500 to-purple-500',
  'from-green-500 to-emerald-600',
];

export function EnquiryFunnelPanel({ funnel }) {
  const navigate = useNavigate();
  if (!funnel || funnel.length === 0) return null;
  const top = funnel[0]?.count || 1;
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-100 dark:border-gray-700 shadow-sm">
      <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-1.5">
        <Zap className="w-4 h-4 text-indigo-500" /> Enquiry funnel · last 30 days
      </h3>
      <div className="space-y-1.5">
        {funnel.map((row, i) => {
          const pct = Math.round((row.count / top) * 100);
          const drop = i > 0 ? funnel[i - 1].count - row.count : 0;
          const dropPct = i > 0 && funnel[i - 1].count ? Math.round(drop / funnel[i - 1].count * 100) : 0;
          return (
            <div key={row.stage}
              onClick={() => navigate(`/engage/enquiries?status=${row.stage}`)}
              className="flex items-center gap-2 text-sm cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/40 rounded-lg px-1 -mx-1"
              title={`Click to see all ${row.count} enquiries at ${ENGAGE_STATUS_LABEL[row.stage] || row.stage}`}>
              <span className="w-40 flex-shrink-0 text-[12px] text-gray-700 dark:text-gray-300 capitalize">
                {ENGAGE_STATUS_LABEL[row.stage] || row.stage.replace(/_/g, ' ')}
              </span>
              <div className="flex-1 h-6 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden relative">
                <div className={cn('h-full bg-gradient-to-r rounded-full transition-all', FUNNEL_TONES[i] || FUNNEL_TONES[0])}
                     style={{ width: `${pct}%` }} />
                <span className="absolute inset-0 flex items-center px-2.5 text-[11px] font-bold text-white mix-blend-difference">
                  {row.count} · {pct}%
                </span>
              </div>
              {i > 0 && drop > 0 ? (
                <span className="w-20 text-[10px] text-red-500 text-right inline-flex items-center justify-end gap-0.5">
                  <TrendingDown className="w-3 h-3" /> −{drop} ({dropPct}%)
                </span>
              ) : <span className="w-20" />}
            </div>
          );
        })}
      </div>
    </div>
  );
}


// ────────────────────────────────────────────────────────────────────────────
// Team workload (top assignees)
// ────────────────────────────────────────────────────────────────────────────

export function TeamWorkloadPanel({ team }) {
  const navigate = useNavigate();
  if (!team || team.length === 0) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-100 dark:border-gray-700 shadow-sm">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-2 flex items-center gap-1.5">
          <Users className="w-4 h-4 text-blue-500" /> Team workload
        </h3>
        <p className="text-sm text-gray-500 text-center py-6 dark:text-gray-400">No assigned enquiries yet.</p>
      </div>
    );
  }
  const maxOpen = Math.max(1, ...team.map(t => t.open_count));

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl overflow-hidden border border-gray-100 dark:border-gray-700 shadow-sm">
      <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-700">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-white flex items-center gap-1.5">
          <Users className="w-4 h-4 text-blue-500" /> Team workload · top assignees
        </h3>
      </div>
      <div className="divide-y divide-gray-100 dark:divide-gray-700">
        {team.map(t => (
          <div key={t.assigned_to_id}
            onClick={() => navigate(`/engage/enquiries?assigned_to=${t.assigned_to_id}`)}
            className="px-4 py-2.5 flex items-center gap-3 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/40"
            title={`Click to see ${t.open_count} open enquiries assigned to ${t.name}`}>
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white text-[11px] font-bold flex-shrink-0">
              {(t.name || '?').charAt(0).toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-gray-900 dark:text-white truncate">{t.name}</div>
              <div className="flex items-center gap-2 mt-0.5">
                <div className="flex-1 h-1.5 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden max-w-[200px]">
                  <div className="h-full bg-gradient-to-r from-blue-400 to-indigo-500 rounded-full"
                       style={{ width: `${(t.open_count / maxOpen) * 100}%` }} />
                </div>
                <span className="text-[10px] text-gray-500 whitespace-nowrap">{t.open_count} open · {t.hot} hot</span>
              </div>
            </div>
            <div className="text-right flex-shrink-0">
              <div className={cn('text-[10px] uppercase font-medium', t.overdue > 0 ? 'text-red-500' : 'text-gray-500')}>
                {t.overdue > 0 ? `${t.overdue} overdue` : 'on-track'}
              </div>
              <div className="text-[11px] font-bold text-emerald-600 dark:text-emerald-400 mt-0.5 inline-flex items-center gap-0.5">
                <Percent className="w-2.5 h-2.5" />{t.conversion_rate}% (30d)
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}


// ────────────────────────────────────────────────────────────────────────────
// Referral sources — one big panel with mini-cards per channel
// ────────────────────────────────────────────────────────────────────────────

function ReferralCard({ title, icon: Icon, tone, children, viewAllHref }) {
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-100 dark:border-gray-700 shadow-sm flex flex-col min-h-[220px]">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-white flex items-center gap-1.5">
          <span className={cn('w-6 h-6 rounded-md flex items-center justify-center', tone)}>
            <Icon className="w-3.5 h-3.5" />
          </span>
          {title}
        </h3>
        {viewAllHref && (
          <Link to={viewAllHref} className="text-[10px] text-gray-500 hover:text-blue-600 dark:hover:text-blue-400 inline-flex items-center gap-0.5">
            View all <ChevronRight className="w-3 h-3" />
          </Link>
        )}
      </div>
      <div className="flex-1">{children}</div>
    </div>
  );
}


export function ReferralSourcesPanel({ referralSources }) {
  if (!referralSources) return null;
  const docs = referralSources.doctors      || {};
  const amb  = referralSources.ambulance    || {};
  const corp = referralSources.corporate    || {};
  const camps = referralSources.camps       || {};
  const mkt  = referralSources.digital_marketing || {};

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between border-t border-gray-200 dark:border-gray-700 pt-5">
        <h2 className="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2">
          <Award className="w-5 h-5 text-orange-500" /> Referral sources · at a glance
        </h2>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {/* Referring Doctors */}
        <ReferralCard
          title="Referring Doctors"
          icon={Stethoscope}
          tone="bg-purple-100 text-purple-600 dark:bg-purple-900/30 dark:text-purple-400"
          viewAllHref="/engage/referring-doctors"
        >
          <div className="flex items-center gap-3 mb-2 text-[11px]">
            <div className="flex-1">
              <div className="text-gray-500">Doctors</div>
              <div className="text-lg font-bold text-gray-900 dark:text-white">{docs.total_doctors || 0}</div>
            </div>
            <div className="flex-1 border-l border-gray-100 dark:border-gray-700 pl-3">
              <div className="text-gray-500">Owed</div>
              <div className="text-lg font-bold text-amber-600 dark:text-amber-400">
                ₹ {formatINR(docs.total_incentive_owed)}
              </div>
            </div>
          </div>
          <div className="space-y-1">
            {(docs.top || []).slice(0, 5).map(d => (
              <Link key={d.id} to={`/engage/referring-doctors/${d.id}`}
                className="flex items-center gap-2 text-[12px] hover:bg-gray-50 dark:hover:bg-gray-700/40 rounded px-1.5 py-1">
                <span className="flex-1 truncate text-gray-800 dark:text-gray-200 font-medium">{d.name}</span>
                <span className="text-gray-500">{d.referrals_converted}/{d.referrals_total}</span>
                {d.incentive_unpaid > 0 && (
                  <span className="text-amber-600 dark:text-amber-400 text-[10px]">
                    ₹{formatINR(d.incentive_unpaid)}
                  </span>
                )}
              </Link>
            ))}
            {(docs.top || []).length === 0 && (
              <p className="text-[11px] text-gray-500 text-center py-2 dark:text-gray-400">No referring doctors yet.</p>
            )}
          </div>
        </ReferralCard>

        {/* Ambulance Partners */}
        <ReferralCard
          title="Ambulance Partners"
          icon={Ambulance}
          tone="bg-orange-100 text-orange-600 dark:bg-orange-900/30 dark:text-orange-400"
          viewAllHref="/engage/ambulance-providers"
        >
          <div className="flex items-center gap-3 mb-2 text-[11px]">
            <div className="flex-1">
              <div className="text-gray-500">Providers</div>
              <div className="text-lg font-bold text-gray-900 dark:text-white">{amb.total_providers || 0}</div>
            </div>
            <div className="flex-1 border-l border-gray-100 dark:border-gray-700 pl-3">
              <div className="text-gray-500">Owed</div>
              <div className="text-lg font-bold text-amber-600 dark:text-amber-400">
                ₹ {formatINR(amb.total_incentive_owed)}
              </div>
            </div>
          </div>
          <div className="space-y-1">
            {(amb.top || []).slice(0, 3).map(a => (
              <Link key={a.id} to={`/engage/ambulance-providers/${a.id}`}
                className="flex items-center gap-2 text-[12px] hover:bg-gray-50 dark:hover:bg-gray-700/40 rounded px-1.5 py-1">
                <span className="flex-1 truncate text-gray-800 dark:text-gray-200 font-medium">{a.name}</span>
                <span className="text-gray-500">{a.referrals_total} refs</span>
                {a.incentive_unpaid > 0 && (
                  <span className="text-amber-600 dark:text-amber-400 text-[10px]">
                    ₹{formatINR(a.incentive_unpaid)}
                  </span>
                )}
              </Link>
            ))}
            {(amb.top || []).length === 0 && (
              <p className="text-[11px] text-gray-500 text-center py-2 dark:text-gray-400">No ambulance referrals yet.</p>
            )}
          </div>
        </ReferralCard>

        {/* Corporate Accounts */}
        <ReferralCard
          title="Corporate Accounts"
          icon={Briefcase}
          tone="bg-teal-100 text-teal-600 dark:bg-teal-900/30 dark:text-teal-400"
          viewAllHref="/engage/corporate-accounts"
        >
          <div className="flex items-center gap-3 mb-2 text-[11px]">
            <div className="flex-1">
              <div className="text-gray-500">Active</div>
              <div className="text-lg font-bold text-gray-900 dark:text-white">
                {corp.active || 0} <span className="text-[10px] text-gray-500">/ {corp.total || 0}</span>
              </div>
            </div>
            <div className="flex-1 border-l border-gray-100 dark:border-gray-700 pl-3">
              <div className="text-gray-500">Expiring 30d</div>
              <div className={cn('text-lg font-bold', corp.expiring_30d > 0 ? 'text-red-600 dark:text-red-400' : 'text-gray-500')}>
                {corp.expiring_30d || 0}
              </div>
            </div>
          </div>
          <div className="space-y-1">
            {(corp.expiring_samples || []).slice(0, 4).map(c => (
              <Link key={c.id} to={`/engage/corporate-accounts/${c.id}`}
                className="flex items-center gap-2 text-[12px] hover:bg-gray-50 dark:hover:bg-gray-700/40 rounded px-1.5 py-1">
                <CalendarClock className="w-3 h-3 text-gray-400 flex-shrink-0" />
                <span className="flex-1 truncate text-gray-800 dark:text-gray-200 font-medium">{c.company_name}</span>
                <span className={cn('text-[10px] font-medium',
                  c.days_left <= 30 ? 'text-red-500' : c.days_left <= 90 ? 'text-amber-500' : 'text-gray-500')}>
                  {c.days_left}d left
                </span>
              </Link>
            ))}
            {(corp.expiring_samples || []).length === 0 && (
              <p className="text-[11px] text-gray-500 text-center py-2 dark:text-gray-400">No renewals due in 90 days.</p>
            )}
          </div>
        </ReferralCard>

        {/* Health Camps */}
        <ReferralCard
          title="Health Camps"
          icon={Target}
          tone="bg-pink-100 text-pink-600 dark:bg-pink-900/30 dark:text-pink-400"
          viewAllHref="/engage/health-camps"
        >
          <div className="flex items-center gap-3 mb-2 text-[11px]">
            <div className="flex-1">
              <div className="text-gray-500">Total</div>
              <div className="text-lg font-bold text-gray-900 dark:text-white">{camps.total || 0}</div>
            </div>
            <div className="flex-1 border-l border-gray-100 dark:border-gray-700 pl-3">
              <div className="text-gray-500">Upcoming</div>
              <div className="text-lg font-bold text-pink-600 dark:text-pink-400">{camps.upcoming || 0}</div>
            </div>
          </div>
          <div className="space-y-1">
            {(camps.recent || []).slice(0, 3).map(c => (
              <Link key={c.id} to={`/engage/health-camps/${c.id}`}
                className="block hover:bg-gray-50 dark:hover:bg-gray-700/40 rounded px-1.5 py-1">
                <div className="flex items-center gap-2 text-[12px]">
                  <span className="flex-1 truncate text-gray-800 dark:text-gray-200 font-medium">{c.name}</span>
                  <span className="text-gray-500">{c.enquiries_converted}/{c.actual_footfall}</span>
                </div>
                <div className="text-[10px] text-gray-500">
                  {fmtDate(c.start_datetime)} · CPP ₹{formatINR(c.cost_per_patient)}
                </div>
              </Link>
            ))}
            {(camps.recent || []).length === 0 && (
              <p className="text-[11px] text-gray-500 text-center py-2 dark:text-gray-400">No recent camps yet.</p>
            )}
          </div>
        </ReferralCard>

        {/* Digital Marketing */}
        <ReferralCard
          title="Digital Marketing"
          icon={LineChartIcon}
          tone="bg-cyan-100 text-cyan-600 dark:bg-cyan-900/30 dark:text-cyan-400"
          viewAllHref="/engage/digital-marketing"
        >
          <div className="flex items-center gap-3 mb-2 text-[11px]">
            <div className="flex-1">
              <div className="text-gray-500">Active</div>
              <div className="text-lg font-bold text-gray-900 dark:text-white">{mkt.active_campaigns || 0}</div>
            </div>
            <div className="flex-1 border-l border-gray-100 dark:border-gray-700 pl-3">
              <div className="text-gray-500">Spend</div>
              <div className="text-lg font-bold text-gray-900 dark:text-white">₹ {formatINR(mkt.total_spend)}</div>
            </div>
          </div>
          <div className="space-y-1">
            {(mkt.top_campaigns || []).slice(0, 3).map(c => (
              <Link key={c.id} to={`/engage/digital-marketing/${c.id}`}
                className="block hover:bg-gray-50 dark:hover:bg-gray-700/40 rounded px-1.5 py-1">
                <div className="flex items-center gap-2 text-[12px]">
                  <span className="flex-1 truncate text-gray-800 dark:text-gray-200 font-medium">{c.name}</span>
                  <span className="text-gray-500">{c.linked_enquiries || 0} leads</span>
                </div>
                <div className="text-[10px] text-gray-500 truncate">
                  {c.platform} · CPL ₹{formatINR(c.cpl)} · {c.converted} conv.
                </div>
              </Link>
            ))}
            {(mkt.top_campaigns || []).length === 0 && (
              <p className="text-[11px] text-gray-500 text-center py-2 dark:text-gray-400">No active campaigns yet.</p>
            )}
          </div>
        </ReferralCard>

        {/* Combined incentives summary card */}
        <ReferralCard
          title="Incentives to pay"
          icon={IndianRupee}
          tone="bg-amber-100 text-amber-600 dark:bg-amber-900/30 dark:text-amber-400"
        >
          <div className="flex flex-col justify-center h-full">
            <div className="text-3xl font-bold text-amber-600 dark:text-amber-400">
              ₹ {formatINR((docs.total_incentive_owed || 0) + (amb.total_incentive_owed || 0))}
            </div>
            <p className="text-[11px] text-gray-500 mt-1">
              Doctors: ₹{formatINR(docs.total_incentive_owed)} · Ambulance: ₹{formatINR(amb.total_incentive_owed)}
            </p>
            <p className="text-[10px] text-gray-400 mt-2">
              Total pending payout across all referral channels.
            </p>
          </div>
        </ReferralCard>
      </div>
    </div>
  );
}


// ────────────────────────────────────────────────────────────────────────────
// Hot patients — top 10 most-engaged active enquiries
// ────────────────────────────────────────────────────────────────────────────

const TEMPERATURE_META = {
  hot:  { icon: Flame,     label: 'Hot',  tone: 'bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400' },
  warm: { icon: Sparkles,  label: 'Warm', tone: 'bg-amber-100 text-amber-600 dark:bg-amber-900/30 dark:text-amber-400' },
  cool: { icon: Snowflake, label: 'Cool', tone: 'bg-slate-100 text-slate-600 dark:bg-slate-900/30 dark:text-slate-400' },
};

export function HotPatientsPanel({ patients }) {
  if (!patients || patients.length === 0) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-100 dark:border-gray-700 shadow-sm">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-2 flex items-center gap-1.5">
          <Flame className="w-4 h-4 text-red-500" /> Hot patients
        </h3>
        <p className="text-sm text-gray-500 text-center py-6 dark:text-gray-400">No engaged enquiries in the last 14 days.</p>
      </div>
    );
  }

  const maxScore = Math.max(1, ...patients.map(p => p.activity_14d || 0));

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl overflow-hidden border border-gray-100 dark:border-gray-700 shadow-sm">
      <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-white flex items-center gap-1.5">
          <Flame className="w-4 h-4 text-red-500" /> Hot patients · most-engaged in the last 14 days
        </h3>
        <Link to="/engage/reports/engagement" className="text-[11px] text-blue-600 hover:text-blue-700 dark:text-blue-400 inline-flex items-center gap-0.5">
          Full report <ChevronRight className="w-3 h-3" />
        </Link>
      </div>
      <div className="divide-y divide-gray-100 dark:divide-gray-700">
        {patients.map(p => {
          const T = TEMPERATURE_META[p.temperature] || TEMPERATURE_META.cool;
          const pct = ((p.activity_14d || 0) / maxScore) * 100;
          return (
            <Link
              key={p.id}
              to={`/engage/enquiries/${p.id}`}
              className="flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 dark:hover:bg-gray-700/40"
            >
              <div className={cn('w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0', T.tone)}>
                <T.icon className="w-4 h-4" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-gray-900 dark:text-white truncate">{p.name}</span>
                  <span className={cn('inline-block px-1.5 py-0.5 rounded-full text-[10px] font-medium', getEngageStatusBadge(p.status))}>
                    {ENGAGE_STATUS_LABEL[p.status] || p.status}
                  </span>
                  {p.source && (
                    <span className={cn('inline-block px-1.5 py-0.5 rounded-full text-[10px] font-medium', getEngageSourceBadge(p.source))}>
                      {ENGAGE_SOURCE_LABEL[p.source] || p.source}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2 mt-1">
                  <div className="flex-1 h-1 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden max-w-[200px]">
                    <div className={cn(
                      'h-full rounded-full',
                      p.temperature === 'hot'  ? 'bg-gradient-to-r from-orange-400 to-red-500' :
                      p.temperature === 'warm' ? 'bg-gradient-to-r from-yellow-400 to-amber-500' :
                                                 'bg-gradient-to-r from-slate-300 to-slate-400',
                    )} style={{ width: `${pct}%` }} />
                  </div>
                  <span className="text-[10px] text-gray-500 whitespace-nowrap">
                    {p.activity_14d} touches
                    {p.department ? ` · ${p.department}` : ''}
                  </span>
                </div>
              </div>
              <div className="text-right flex-shrink-0">
                <div className="text-[10px] text-gray-500">{p.mobile || '—'}</div>
                {p.followup_datetime && (
                  <div className="text-[10px] text-blue-600 dark:text-blue-400 mt-0.5">
                    Follow-up {new Date(p.followup_datetime).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}
                  </div>
                )}
              </div>
            </Link>
          );
        })}
      </div>
    </div>
  );
}


// ────────────────────────────────────────────────────────────────────────────
// 30-day trend — new enquiries vs converted per day
// ────────────────────────────────────────────────────────────────────────────

export function EnquiryTrendPanel({ trend }) {
  const stats = useMemo(() => {
    if (!trend || trend.length === 0) return null;
    const W = 100, H = 40;
    const localMax = Math.max(1, ...trend.map(d => Math.max(d.new, d.converted)));
    const step = W / Math.max(1, trend.length - 1);

    const buildPath = (key) => trend.map((d, i) => {
      const x = i * step;
      const y = H - (d[key] / localMax) * H;
      return `${i ? 'L' : 'M'}${x.toFixed(2)} ${y.toFixed(2)}`;
    }).join(' ');

    const newPath  = buildPath('new');
    const convPath = buildPath('converted');
    const newArea  = newPath ? `${newPath} L 100 40 L 0 40 Z` : '';

    return {
      newPath, convPath, newArea, max: localMax,
      points: trend.map((d, i) => ({
        x: i * step,
        yNew:  H - (d.new / localMax) * H,
        yConv: H - (d.converted / localMax) * H,
        ...d,
      })),
      totalNew:  trend.reduce((s, d) => s + d.new, 0),
      totalConv: trend.reduce((s, d) => s + d.converted, 0),
    };
  }, [trend]);

  if (!stats) return null;

  const convRate = stats.totalNew ? Math.round(stats.totalConv / stats.totalNew * 100) : 0;
  const ticks = [trend[0]?.date, trend[Math.floor(trend.length / 2)]?.date, trend[trend.length - 1]?.date];

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-100 dark:border-gray-700 shadow-sm">
      <div className="flex items-start justify-between mb-3">
        <div>
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white flex items-center gap-1.5">
            <TrendingUp className="w-4 h-4 text-blue-500" /> Enquiry trend · last 30 days
          </h3>
          <p className="text-[11px] text-gray-500 mt-0.5">
            {stats.totalNew} new · {stats.totalConv} converted · {convRate}% conv.
          </p>
        </div>
        <div className="flex items-center gap-3 text-[10px]">
          <span className="inline-flex items-center gap-1 text-blue-600 dark:text-blue-400">
            <span className="w-2 h-2 bg-blue-500 rounded-sm" /> New
          </span>
          <span className="inline-flex items-center gap-1 text-green-600 dark:text-green-400">
            <span className="w-2 h-2 bg-green-500 rounded-sm" /> Converted
          </span>
        </div>
      </div>
      <svg viewBox="0 0 100 40" preserveAspectRatio="none" className="w-full h-28">
        <defs>
          <linearGradient id="trendNewArea" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%"   stopColor="#3b82f6" stopOpacity="0.30" />
            <stop offset="100%" stopColor="#3b82f6" stopOpacity="0" />
          </linearGradient>
        </defs>
        {stats.newArea  && <path d={stats.newArea}  fill="url(#trendNewArea)" />}
        {stats.newPath  && <path d={stats.newPath}  stroke="#3b82f6" strokeWidth="1.5" fill="none" vectorEffect="non-scaling-stroke" />}
        {stats.convPath && <path d={stats.convPath} stroke="#10b981" strokeWidth="1.5" fill="none" strokeDasharray="1 1" vectorEffect="non-scaling-stroke" />}
        {stats.points.map((p, i) => (
          <g key={i}>
            {p.new > 0      && <circle cx={p.x} cy={p.yNew}  r="1.2" fill="#3b82f6" vectorEffect="non-scaling-stroke"><title>{`${p.date}: ${p.new} new`}</title></circle>}
            {p.converted > 0 && <circle cx={p.x} cy={p.yConv} r="1.6" fill="#10b981" vectorEffect="non-scaling-stroke"><title>{`${p.date}: ${p.converted} converted`}</title></circle>}
          </g>
        ))}
      </svg>
      <div className="flex items-center justify-between text-[10px] text-gray-400 mt-1">
        {ticks.filter(Boolean).map((d, i) => (
          <span key={i}>{new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}</span>
        ))}
      </div>
    </div>
  );
}


// ────────────────────────────────────────────────────────────────────────────
// Source-wise revenue projection
// ────────────────────────────────────────────────────────────────────────────

const SOURCE_TONE = {
  doctor_referral:      'from-purple-500 to-fuchsia-500',
  ambulance_referral:   'from-orange-500 to-red-500',
  corporate_account:    'from-teal-500 to-emerald-500',
  health_camp:          'from-pink-500 to-rose-500',
  digital_marketing:    'from-cyan-500 to-sky-500',
  website:              'from-blue-500 to-indigo-500',
  whatsapp:             'from-green-500 to-emerald-500',
  phone:                'from-slate-500 to-gray-500',
  walk_in:              'from-amber-500 to-yellow-500',
  staff_referral:       'from-violet-500 to-purple-500',
  insurance:            'from-blue-400 to-cyan-400',
  word_of_mouth:        'from-pink-400 to-rose-400',
  emergency_108:        'from-red-500 to-rose-600',
  social_media:         'from-fuchsia-500 to-pink-500',
  other:                'from-slate-400 to-slate-500',
};


export function RevenueProjectionPanel({ projection, arppOverride, onArppChange, onOverridesSaved, rangeFrom, rangeTo }) {
  const navigate = useNavigate();
  const [modalOpen, setModalOpen] = useState(false);
  const [catalogOpen, setCatalogOpen] = useState(false);

  if (!projection) return null;

  const { assumptions = {}, totals = {}, by_source = [] } = projection;
  const perDept = assumptions.per_department_arpp || {};
  const overrideCount = Object.keys(perDept).length;
  const rows = by_source.filter(r => r.projected_total > 0 || r.pipeline > 0 || r.converted_all > 0);
  if (rows.length === 0 && (totals.projected_total || 0) === 0) return null;

  const maxProjected = Math.max(1, ...by_source.map(r => r.projected_total || 0));

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm overflow-hidden">
      {/* Header */}
      <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-700 flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-sm font-semibold text-gray-900 dark:text-white flex items-center gap-1.5">
            <Wallet className="w-4 h-4 text-emerald-500" /> Revenue projection by source
          </h2>
          <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5 flex items-center gap-1">
            <Info className="w-3 h-3" />
            Realised = converted × ARPP. Pipeline = active × source conv-rate × ARPP.
            <HelpBadge content={
              <>
                <div className="font-semibold mb-1">Value resolution per enquiry:</div>
                <div className="ml-1 space-y-1">
                  <div><span className="text-green-300 font-bold">1.</span> <b>Quoted:</b> per-enquiry <code>expected_treatment_value</code> if set.</div>
                  <div><span className="text-teal-300 font-bold">2.</span> <b>Catalog:</b> match on <code>procedure_of_interest</code> → catalog default.</div>
                  <div><span className="text-gray-300 font-bold dark:text-gray-600">3.</span> <b>Dept ARPP:</b> per-department override if configured.</div>
                  <div><span className="text-gray-300 font-bold dark:text-gray-600">4.</span> <b>Global ARPP:</b> the fallback in the header.</div>
                </div>
                <div className="mt-2 pt-2 border-t border-gray-700 text-gray-400">
                  Pipeline is scaled by each source's historical conversion rate so the number reflects what's actually likely to land — not the theoretical maximum.
                </div>
              </>
            } side="bottom" />
            {overrideCount > 0 && (
              <span className="ml-1 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-300 text-[10px] font-medium">
                {overrideCount} dept override{overrideCount !== 1 ? 's' : ''}
              </span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <label className="inline-flex items-center gap-1.5 rounded-lg bg-gray-50 dark:bg-gray-900/40 border border-gray-200 dark:border-gray-700 px-2 py-1"
                 title="Fallback ARPP for enquiries whose department has no override">
            <span className="text-[10px] uppercase tracking-wide text-gray-500 font-medium">Global ARPP ₹</span>
            <input
              type="number"
              value={arppOverride ?? assumptions.avg_arpp ?? 15000}
              onChange={(e) => onArppChange && onArppChange(Number(e.target.value) || 0)}
              step="500" min="0"
              className="w-24 bg-transparent text-sm font-medium text-gray-900 dark:text-white focus:outline-none"
            />
          </label>
          <button
            onClick={() => setModalOpen(true)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm text-emerald-700 dark:text-emerald-300 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800/60 hover:bg-emerald-100 dark:hover:bg-emerald-900/30"
          >
            <Settings className="w-3.5 h-3.5" /> Department ARPPs
          </button>
          <button
            onClick={() => setCatalogOpen(true)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm text-blue-700 dark:text-blue-300 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800/60 hover:bg-blue-100 dark:hover:bg-blue-900/30"
          >
            <Settings className="w-3.5 h-3.5" /> Procedure catalog
          </button>
        </div>
      </div>

      {modalOpen && (
        <DepartmentARPPModal
          onClose={() => setModalOpen(false)}
          onSaved={() => { setModalOpen(false); onOverridesSaved && onOverridesSaved(); }}
        />
      )}
      {catalogOpen && (
        <ProcedureCatalogModal
          onClose={() => setCatalogOpen(false)}
          onSaved={() => { setCatalogOpen(false); onOverridesSaved && onOverridesSaved(); }}
        />
      )}

      {/* Headline numbers */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 p-4 bg-gray-50 dark:bg-gray-900/40 border-b border-gray-100 dark:border-gray-700">
        <div>
          <div className="text-[10px] uppercase tracking-wide text-gray-500 font-medium inline-flex items-center gap-1">
            Realised (period)
            <HelpBadge content={
              <>
                <b>Booked revenue</b> from enquiries that <b>converted to patients</b> during the selected period.
                <br /><br />
                For each converted enquiry:<br />
                <span className="text-green-300">•</span> quoted amount if set,<br />
                <span className="text-teal-300">•</span> else procedure catalog default,<br />
                <span className="text-gray-400">•</span> else department / global ARPP.
              </>
            } />
          </div>
          <div className="text-xl font-bold text-emerald-600 dark:text-emerald-400 mt-0.5">
            ₹ {formatINR(totals.realised_revenue)}
          </div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wide text-gray-500 font-medium inline-flex items-center gap-1">
            Pipeline value
            <HelpBadge content={
              <>
                <b>Expected revenue</b> from currently-active enquiries — the ones still in the pipeline.
                <br /><br />
                <span className="font-mono text-[10px]">Σ (value × source_conv_rate)</span>
                <br /><br />
                Each enquiry's value uses the same quote → catalog → ARPP fallback chain.
                The historical conversion rate for that source scales the sum down to a realistic expectation.
              </>
            } />
          </div>
          <div className="text-xl font-bold text-blue-600 dark:text-blue-400 mt-0.5">
            ₹ {formatINR(totals.pipeline_revenue)}
          </div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wide text-gray-500 font-medium inline-flex items-center gap-1">
            Projected total
            <HelpBadge content={
              <>
                <b>Realised + Pipeline</b> — the total revenue we expect from the current period's converted patients <em>plus</em> whatever the active pipeline is likely to bring in.
              </>
            } />
          </div>
          <div className="text-xl font-bold text-gray-900 dark:text-white mt-0.5">
            ₹ {formatINR(totals.projected_total)}
          </div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wide text-gray-500 font-medium inline-flex items-center gap-1">
            Paid-channel spend
            <HelpBadge content={
              <>
                Sum of money already committed to paid acquisition channels:
                <br /><br />
                • <b>Health camps</b> — actual spend<br />
                • <b>Digital marketing</b> — campaign spend<br />
                • <b>Doctor referrals</b> — unpaid incentives<br />
                • <b>Ambulance referrals</b> — unpaid incentives
              </>
            } />
          </div>
          <div className="text-xl font-bold text-amber-600 dark:text-amber-400 mt-0.5">
            ₹ {formatINR(totals.total_spend)}
          </div>
          {totals.total_spend > 0 && (
            <div className="text-[10px] text-gray-500 mt-0.5 inline-flex items-center gap-1">
              Blended ROI: <span className="font-bold">
                {(totals.projected_total / totals.total_spend).toFixed(2)}×
              </span>
              <HelpBadge content={
                <>
                  <b>Projected total ÷ Paid-channel spend</b>
                  <br /><br />
                  &gt; 3× is healthy · 1-3× is amber · &lt; 1× means you're spending more than you'll earn back from these channels.
                </>
              } />
            </div>
          )}
        </div>
      </div>

      {/* Legend for the Data-quality column */}
      <div className="px-4 py-1.5 border-b border-gray-100 dark:border-gray-700 flex items-center gap-3 text-[10px] text-gray-500 dark:text-gray-400">
        <span className="uppercase tracking-wide font-medium">Data-quality legend:</span>
        <span className="inline-flex items-center gap-1"><span className="w-2 h-2 bg-green-500 rounded-sm" /> Quoted (per-enquiry)</span>
        <span className="inline-flex items-center gap-1"><span className="w-2 h-2 bg-teal-400 rounded-sm" /> Catalog (procedure match)</span>
        <span className="inline-flex items-center gap-1"><span className="w-2 h-2 bg-gray-300 dark:bg-gray-600 rounded-sm" /> Fallback (ARPP)</span>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 dark:bg-gray-900/40 text-[10px] uppercase tracking-wide text-gray-500">
            <tr>
              <th className="px-4 py-2 text-left font-medium">Source</th>
              <th className="px-4 py-2 text-right font-medium">Conv-rate</th>
              <th className="px-4 py-2 text-right font-medium">Realised ₹</th>
              <th className="px-4 py-2 text-right font-medium">Pipeline</th>
              <th className="px-4 py-2 text-right font-medium">Pipeline ₹</th>
              <th className="px-4 py-2 text-right font-medium min-w-[130px]">
                <span className="inline-flex items-center gap-1">
                  Data quality
                  <HelpBadge content={
                    <>
                      How much of this row's projection is based on actual quotes vs statistical averages.
                      <br /><br />
                      <span className="text-green-300 font-bold">Green</span> = per-enquiry quote entered by staff (most accurate)<br />
                      <span className="text-teal-300 font-bold">Teal</span> = procedure name matched to catalog default<br />
                      <span className="text-gray-300 font-bold dark:text-gray-600">Gray</span> = ARPP fallback (departmental or global average)
                      <br /><br />
                      Aim for high green + teal. If a source is mostly gray, the projection is soft — nudge staff to fill quotes on new enquiries.
                    </>
                  } side="left" />
                </span>
              </th>
              <th className="px-4 py-2 text-right font-medium">Spend ₹</th>
              <th className="px-4 py-2 text-right font-medium">ROI</th>
              <th className="px-4 py-2 text-left font-medium min-w-[180px]">Projected total</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
            {rows.length === 0 ? (
              <tr><td colSpan={9} className="px-4 py-8 text-center text-gray-500 dark:text-gray-400">No source data in the selected period.</td></tr>
            ) : rows.map(r => {
              const sharePct = (r.projected_total / maxProjected) * 100;
              const totalProjectedPct = totals.projected_total ? Math.round(r.projected_total / totals.projected_total * 100) : 0;
              const tone = SOURCE_TONE[r.source] || SOURCE_TONE.other;
              const quotedTotal      = (r.quoted_realised  || 0) + (r.quoted_pipeline  || 0);
              const catalogTotal     = (r.catalog_realised || 0) + (r.catalog_pipeline || 0);
              const contributingTotal = (r.period_converted || 0) + (r.pipeline || 0);
              const estimatedTotal   = Math.max(0, contributingTotal - quotedTotal - catalogTotal);
              const quotedPct    = contributingTotal ? (quotedTotal    / contributingTotal) * 100 : 0;
              const catalogPct   = contributingTotal ? (catalogTotal   / contributingTotal) * 100 : 0;
              const estimatedPct = contributingTotal ? (estimatedTotal / contributingTotal) * 100 : 0;
              const knownPct     = Math.round(quotedPct + catalogPct);   // headline %
              return (
                <tr
                  key={r.source}
                  onClick={() => {
                    const qs = new URLSearchParams();
                    if (rangeFrom) qs.set('from', rangeFrom);
                    if (rangeTo)   qs.set('to',   rangeTo);
                    if (arppOverride) qs.set('arpp', String(arppOverride));
                    navigate(`/engage/reports/revenue-source/${r.source}?${qs.toString()}`);
                  }}
                  className="hover:bg-gray-50 dark:hover:bg-gray-700/40 cursor-pointer"
                  title="Click for deep-dive"
                >
                  <td className="px-4 py-2.5">
                    <span className={cn('inline-block px-2 py-0.5 rounded-full text-[10px] font-medium', getEngageSourceBadge(r.source))}>
                      {ENGAGE_SOURCE_LABEL[r.source] || r.source}
                    </span>
                    <div className="text-[10px] text-gray-500 mt-0.5">
                      {r.converted_all}/{r.total_all} converted all-time
                    </div>
                  </td>
                  <td className="px-4 py-2.5 text-right">
                    <span className={cn(
                      'inline-block px-1.5 py-0.5 rounded-full text-[10px] font-medium',
                      r.conversion_rate >= 30 ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300' :
                      r.conversion_rate >= 15 ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300' :
                                                'bg-slate-100 text-slate-700 dark:bg-slate-900/30 dark:text-slate-300',
                    )}>
                      {r.conversion_rate}%
                    </span>
                  </td>
                  <td className="px-4 py-2.5 text-right font-medium text-emerald-600 dark:text-emerald-400">
                    ₹ {formatINR(r.realised_revenue)}
                  </td>
                  <td className="px-4 py-2.5 text-right text-gray-700 dark:text-gray-300">{r.pipeline}</td>
                  <td className="px-4 py-2.5 text-right text-blue-600 dark:text-blue-400">
                    ₹ {formatINR(r.pipeline_revenue)}
                  </td>
                  <td className="px-4 py-2.5">
                    {contributingTotal === 0 ? (
                      <span className="text-gray-400 float-right">—</span>
                    ) : (
                      <InfoTooltip
                        side="left"
                        content={
                          <>
                            <div className="font-semibold mb-1">
                              {ENGAGE_SOURCE_LABEL[r.source] || r.source} — data quality
                            </div>
                            <div className="flex items-center gap-2 my-1.5">
                              <span className="w-2 h-2 bg-green-500 rounded-sm" />
                              <span className="flex-1"><b>{quotedTotal}</b> quoted</span>
                              <span className="text-gray-400">{Math.round(quotedPct)}%</span>
                            </div>
                            <div className="flex items-center gap-2 my-1.5">
                              <span className="w-2 h-2 bg-teal-400 rounded-sm" />
                              <span className="flex-1"><b>{catalogTotal}</b> catalog-matched</span>
                              <span className="text-gray-400">{Math.round(catalogPct)}%</span>
                            </div>
                            <div className="flex items-center gap-2 my-1.5">
                              <span className="w-2 h-2 bg-gray-300 rounded-sm" />
                              <span className="flex-1"><b>{estimatedTotal}</b> ARPP fallback</span>
                              <span className="text-gray-400">{Math.round(estimatedPct)}%</span>
                            </div>
                            <div className="border-t border-gray-700 mt-2 pt-1.5 text-gray-400">
                              {contributingTotal} enquiries · {r.period_converted} converted this period + {r.pipeline} in pipeline
                            </div>
                          </>
                        }
                      >
                        <div className="flex flex-col items-end gap-0.5 cursor-help">
                          <div className="flex items-center gap-1.5">
                            <div className="w-16 h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden flex">
                              {quotedPct    > 0 && <div className="h-full bg-green-500" style={{ width: `${quotedPct}%` }} />}
                              {catalogPct   > 0 && <div className="h-full bg-teal-400"  style={{ width: `${catalogPct}%` }} />}
                              {estimatedPct > 0 && <div className="h-full bg-gray-300 dark:bg-gray-600" style={{ width: `${estimatedPct}%` }} />}
                            </div>
                            <span className={cn(
                              'text-[10px] font-medium tabular-nums',
                              knownPct >= 75 ? 'text-green-600 dark:text-green-400' :
                              knownPct >= 40 ? 'text-teal-600 dark:text-teal-400' :
                                               'text-gray-500 dark:text-gray-400',
                            )}>{knownPct}%</span>
                          </div>
                          <div className="text-[9px] text-gray-400 tabular-nums">
                            <span className="text-green-600 dark:text-green-400">{quotedTotal}</span>
                            {' + '}
                            <span className="text-teal-600 dark:text-teal-400">{catalogTotal}</span>
                            {' / '}{contributingTotal}
                          </div>
                        </div>
                      </InfoTooltip>
                    )}
                  </td>
                  <td className="px-4 py-2.5 text-right text-gray-700 dark:text-gray-300">
                    {r.spend > 0 ? `₹ ${formatINR(r.spend)}` : '—'}
                  </td>
                  <td className="px-4 py-2.5 text-right">
                    {r.roi_multiple != null ? (
                      <span className={cn(
                        'inline-block px-1.5 py-0.5 rounded-full text-[10px] font-bold',
                        r.roi_multiple >= 3 ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300' :
                        r.roi_multiple >= 1 ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300' :
                                              'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
                      )}>
                        {r.roi_multiple.toFixed(2)}×
                      </span>
                    ) : <span className="text-gray-400">—</span>}
                  </td>
                  <td className="px-4 py-2.5">
                    <div className="flex items-center gap-2">
                      <div className="flex-1 h-4 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden relative min-w-[100px]">
                        <div className={cn('h-full bg-gradient-to-r rounded-full transition-all', tone)}
                             style={{ width: `${sharePct}%` }} />
                        <span className="absolute inset-0 flex items-center px-2 text-[10px] font-bold text-white mix-blend-difference">
                          ₹ {formatINR(r.projected_total)}
                        </span>
                      </div>
                      <span className="text-[10px] text-gray-500 w-8 text-right">{totalProjectedPct}%</span>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}


// ────────────────────────────────────────────────────────────────────────────
// Department ARPP editor — modal
// ────────────────────────────────────────────────────────────────────────────

function DepartmentARPPModal({ onClose, onSaved }) {
  const { fetch: apiFetch, update: apiUpdate } = useApiMethods({ throwOnFail: true });
  const [rows, setRows] = useState(null);   // null=loading, [] or [{...}]
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const resp = await apiFetch('engage/analytics/department-arpp/');
        const list = resp?.data || resp?.data?.data || [];
        setRows(Array.isArray(list) ? list : []);
      } catch (e) {
        setError('Failed to load overrides');
        setRows([]);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const addRow    = () => setRows(r => [...(r || []), { department: '', avg_arpp: 0, notes: '' }]);
  const removeRow = (i) => setRows(r => r.filter((_, idx) => idx !== i));
  const updateRow = (i, patch) => setRows(r => r.map((row, idx) => (idx === i ? { ...row, ...patch } : row)));

  const save = async () => {
    // Basic dedupe / validation
    const seen = new Set();
    for (const r of (rows || [])) {
      const key = (r.department || '').trim().toLowerCase();
      if (!key) continue;
      if (seen.has(key)) {
        ToastMsg('error', `Duplicate department: "${r.department}"`);
        return;
      }
      seen.add(key);
      if (Number(r.avg_arpp) < 0) {
        ToastMsg('error', `ARPP must be non-negative for ${r.department}`);
        return;
      }
    }

    setSaving(true);
    try {
      const cleaned = (rows || []).filter(r => (r.department || '').trim());
      await apiUpdate('engage/analytics/department-arpp/', { rows: cleaned });
      ToastMsg('success', `Saved ${cleaned.length} department override${cleaned.length !== 1 ? 's' : ''}`);
      onSaved && onSaved();
    } catch (e) {
      ToastMsg('error', 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={e => e.stopPropagation()} className="bg-white dark:bg-gray-800 rounded-xl w-full max-w-2xl max-h-[90vh] flex flex-col shadow-xl">
        <div className="p-4 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between">
          <div>
            <h2 className="font-semibold text-gray-900 dark:text-white flex items-center gap-1.5">
              <Wallet className="w-4 h-4 text-emerald-500" /> Department ARPP overrides
            </h2>
            <p className="text-[11px] text-gray-500 mt-0.5">
              Set the avg realised ₹ per converted patient for each specialty. Enquiries whose department matches
              (case-insensitive) will use that value instead of the global ARPP.
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 flex-shrink-0"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-4 flex-1 overflow-y-auto">
          {rows === null ? (
            <div className="flex items-center justify-center py-12 text-gray-500">
              <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading…
            </div>
          ) : (
            <>
              {rows.length === 0 && (
                <div className="text-center py-8 text-sm text-gray-500">
                  No overrides yet. Click "Add department" to start.
                </div>
              )}
              <div className="space-y-2">
                {rows.map((r, i) => (
                  <div key={i} className="flex items-start gap-2 p-2 rounded-lg bg-gray-50 dark:bg-gray-900/40 border border-gray-100 dark:border-gray-700">
                    <div className="flex-1 grid grid-cols-1 sm:grid-cols-5 gap-2">
                      <input
                        type="text"
                        value={r.department || ''}
                        onChange={(e) => updateRow(i, { department: e.target.value })}
                        placeholder="Department (e.g. Cardiology)"
                        className="sm:col-span-2 px-2 py-1.5 text-sm rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white"
                      />
                      <div className="relative">
                        <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400 text-[11px]">₹</span>
                        <input
                          type="number"
                          value={r.avg_arpp ?? 0}
                          onChange={(e) => updateRow(i, { avg_arpp: Number(e.target.value) || 0 })}
                          step="500" min="0"
                          className="w-full pl-5 pr-2 py-1.5 text-sm rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white"
                        />
                      </div>
                      <input
                        type="text"
                        value={r.notes || ''}
                        onChange={(e) => updateRow(i, { notes: e.target.value })}
                        placeholder="Notes (optional)"
                        className="sm:col-span-2 px-2 py-1.5 text-sm rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white"
                      />
                    </div>
                    <button onClick={() => removeRow(i)} className="p-1.5 rounded text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 flex-shrink-0">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
              <button
                onClick={addRow}
                className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm text-emerald-700 dark:text-emerald-300 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800/60 hover:bg-emerald-100 dark:hover:bg-emerald-900/30"
              >
                <Plus className="w-3.5 h-3.5" /> Add department
              </button>
              {error && (
                <p className="text-[11px] text-red-500 mt-3">{error}</p>
              )}
            </>
          )}
        </div>

        <div className="p-4 border-t border-gray-100 dark:border-gray-700 flex items-center justify-end gap-2">
          <button onClick={onClose}
            className="px-3 py-1.5 rounded-lg text-sm text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 hover:bg-gray-50">
            Cancel
          </button>
          <button onClick={save} disabled={saving || rows === null}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm text-white bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 disabled:opacity-50">
            <Save className="w-3.5 h-3.5" /> {saving ? 'Saving…' : 'Save overrides'}
          </button>
        </div>
      </div>
    </div>
  );
}


// ────────────────────────────────────────────────────────────────────────────
// Procedure catalog editor — modal
// ────────────────────────────────────────────────────────────────────────────

function ProcedureCatalogModal({ onClose, onSaved }) {
  const { fetch: apiFetch, update: apiUpdate } = useApiMethods({ throwOnFail: true });
  const [rows, setRows] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const resp = await apiFetch('engage/analytics/procedure-catalog/');
        const list = resp?.data || resp?.data?.data || [];
        setRows(Array.isArray(list) ? list : []);
      } catch (e) {
        setError('Failed to load catalog');
        setRows([]);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const addRow    = () => setRows(r => [...(r || []), { name: '', department: '', default_value: 0, notes: '' }]);
  const removeRow = (i) => setRows(r => r.filter((_, idx) => idx !== i));
  const updateRow = (i, patch) => setRows(r => r.map((row, idx) => (idx === i ? { ...row, ...patch } : row)));

  const save = async () => {
    const seen = new Set();
    for (const r of (rows || [])) {
      const key = (r.name || '').trim().toLowerCase();
      if (!key) continue;
      if (seen.has(key)) {
        ToastMsg('error', `Duplicate procedure: "${r.name}"`);
        return;
      }
      seen.add(key);
      if (Number(r.default_value) < 0) {
        ToastMsg('error', `Default value must be non-negative for ${r.name}`);
        return;
      }
    }

    setSaving(true);
    try {
      const cleaned = (rows || []).filter(r => (r.name || '').trim());
      await apiUpdate('engage/analytics/procedure-catalog/', { rows: cleaned });
      ToastMsg('success', `Saved ${cleaned.length} procedure${cleaned.length !== 1 ? 's' : ''}`);
      onSaved && onSaved();
    } catch (e) {
      ToastMsg('error', 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={e => e.stopPropagation()} className="bg-white dark:bg-gray-800 rounded-xl w-full max-w-3xl max-h-[90vh] flex flex-col shadow-xl">
        <div className="p-4 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between">
          <div>
            <h2 className="font-semibold text-gray-900 dark:text-white flex items-center gap-1.5">
              <Wallet className="w-4 h-4 text-blue-500" /> Procedure catalog
            </h2>
            <p className="text-[11px] text-gray-500 mt-0.5">
              Set the default expected ₹ revenue per patient for each procedure. Staff can pick a procedure
              on the enquiry form and this value auto-populates the quote.
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 flex-shrink-0"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-4 flex-1 overflow-y-auto">
          {rows === null ? (
            <div className="flex items-center justify-center py-12 text-gray-500">
              <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading…
            </div>
          ) : (
            <>
              {rows.length === 0 && (
                <div className="text-center py-8 text-sm text-gray-500">
                  No procedures yet. Click "Add procedure" to start.
                </div>
              )}
              <div className="space-y-2">
                {rows.map((r, i) => (
                  <div key={i} className="flex items-start gap-2 p-2 rounded-lg bg-gray-50 dark:bg-gray-900/40 border border-gray-100 dark:border-gray-700">
                    <div className="flex-1 grid grid-cols-1 sm:grid-cols-7 gap-2">
                      <input
                        type="text"
                        value={r.name || ''}
                        onChange={(e) => updateRow(i, { name: e.target.value })}
                        placeholder="Procedure (e.g. Angioplasty)"
                        className="sm:col-span-2 px-2 py-1.5 text-sm rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white"
                      />
                      <input
                        type="text"
                        value={r.department || ''}
                        onChange={(e) => updateRow(i, { department: e.target.value })}
                        placeholder="Department"
                        className="sm:col-span-2 px-2 py-1.5 text-sm rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white"
                      />
                      <div className="relative sm:col-span-1">
                        <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400 text-[11px]">₹</span>
                        <input
                          type="number"
                          value={r.default_value ?? 0}
                          onChange={(e) => updateRow(i, { default_value: Number(e.target.value) || 0 })}
                          step="500" min="0"
                          className="w-full pl-5 pr-2 py-1.5 text-sm rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white"
                        />
                      </div>
                      <input
                        type="text"
                        value={r.notes || ''}
                        onChange={(e) => updateRow(i, { notes: e.target.value })}
                        placeholder="Notes (optional)"
                        className="sm:col-span-2 px-2 py-1.5 text-sm rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white"
                      />
                    </div>
                    <button onClick={() => removeRow(i)} className="p-1.5 rounded text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 flex-shrink-0">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
              <button
                onClick={addRow}
                className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm text-blue-700 dark:text-blue-300 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800/60 hover:bg-blue-100 dark:hover:bg-blue-900/30"
              >
                <Plus className="w-3.5 h-3.5" /> Add procedure
              </button>
              {error && (
                <p className="text-[11px] text-red-500 mt-3">{error}</p>
              )}
            </>
          )}
        </div>

        <div className="p-4 border-t border-gray-100 dark:border-gray-700 flex items-center justify-end gap-2">
          <button onClick={onClose}
            className="px-3 py-1.5 rounded-lg text-sm text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 hover:bg-gray-50">
            Cancel
          </button>
          <button onClick={save} disabled={saving || rows === null}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm text-white bg-gradient-to-r from-blue-500 to-cyan-600 hover:from-blue-600 hover:to-cyan-700 disabled:opacity-50">
            <Save className="w-3.5 h-3.5" /> {saving ? 'Saving…' : 'Save catalog'}
          </button>
        </div>
      </div>
    </div>
  );
}
